import React, { useState, useEffect, Suspense, lazy } from 'react';
import './App.css';
import hawksLogo from './assets/illawarra-hawks-logo.png';
import GamePlanning from './pages/GamePlanning';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import Rostering from './pages/Rostering';
import People from './pages/People';

import Payroll from './pages/Payroll';
import CoachPlanner from './pages/CoachPlanner';
import RosterView from './pages/RosterView';
import Message from './pages/Message';
import Help from './pages/Help';
import Judiciary from './pages/Judiciary';
import UserProfile from './components/UserProfile';
import { useMsal } from '@azure/msal-react';
import { setAuditUser } from './auditFetch';
import { usePermissions } from './context/PermissionsContext';

// Swish IQ ships as its own chunk so BOMS pages don't pay for it.
const SwishIqAdmin = lazy(() => import('./swishiq/SwishIqAdmin'));

// Composite pages aggregate permissions from multiple pageKey/sectionKey pairs
const COMPOSITE_PAGE_PERMISSIONS = {
  'rosters': [
    { pageKey: 'rosters', sectionKey: 'rosters' },
    // email-rosters removed; the Email Roster action moved to the
    // Message page (gated by message:send-roster).
  ],
  'people': [
    { pageKey: 'admin', sectionKey: 'person' },
    { pageKey: 'admin', sectionKey: 'availability' },
    { pageKey: 'admin', sectionKey: 'participants' },
  ],
  'message': [
    { pageKey: 'message', sectionKey: 'send-roster' },
    { pageKey: 'message', sectionKey: 'custom' },
    { pageKey: 'message', sectionKey: 'bulk-role' },
    { pageKey: 'message', sectionKey: 'bulk-participants' },
  ],
};

const NAV_ITEMS = [
  { key: 'rosters', pageKey: 'rosters', label: 'Home' },
  { key: 'planning', pageKey: 'game-planner', label: 'Game' },
  { key: 'coach-planner', pageKey: 'coach-planner', label: 'Coach' },
  { key: 'rostering', pageKey: 'court-planner', label: 'Court' },
  { key: 'people', pageKey: 'people', label: 'People' },
  { key: 'swishiq', pageKey: 'swishiq', label: 'SwishIQ Trials' },
  { key: 'message', pageKey: 'message', label: 'Message' },
  { key: 'judiciary', pageKey: 'judiciary', label: 'Judiciary' },
{ key: 'reports', pageKey: 'reports', label: 'Reports' },
  { key: 'payroll', pageKey: 'payroll', label: 'Payroll' },
  { key: 'admin', pageKey: 'admin', label: 'Admin' },
  { key: 'help', pageKey: 'help', label: 'Help' },
];

export default function App() {
  const { accounts, instance } = useMsal();
  const { loading, accessDenied, canAccessPage, canView } = usePermissions();

  // Populate audit user identity from O365 account on login
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      const name = account.name || '';
      const email = account.username || '';
      setAuditUser(name, email);

      // Store first/last name for future security model
      const claims = account.idTokenClaims || {};
      localStorage.setItem('userFirstName', claims.given_name || '');
      localStorage.setItem('userLastName', claims.family_name || '');
      localStorage.setItem('userEmail', email);
    }
  }, [accounts]);

  // Check if user can access a page (handles both standard and composite pages)
  const canAccessNavPage = (pageKey) => {
    // Help is always accessible to any signed-in user. The Help page itself
    // filters its sections based on each user's permissions, so users will
    // only see help topics for areas they can actually use.
    if (pageKey === 'help') return true;
    const compositePerms = COMPOSITE_PAGE_PERMISSIONS[pageKey];
    if (compositePerms) {
      // Composite page: accessible if any of its sections are viewable
      return compositePerms.some(p => canView(p.pageKey, p.sectionKey));
    }
    return canAccessPage(pageKey);
  };

  // Filter nav items by permissions
  const visibleNavItems = NAV_ITEMS.filter(item => canAccessNavPage(item.pageKey));

  // Hash-based routing. URL fragment looks like:
  //   #coach-planner                 -> Coach
  //   #people/participants           -> People page, Participants tab
  // The page key is everything before the first slash; sub-pages read the
  // rest themselves via window.location.hash.
  const getPageFromHash = () => {
    const h = window.location.hash || '';
    const main = h.replace(/^#/, '').split('/')[0];
    return main || '';
  };
  const [currentPage, setCurrentPageState] = useState(getPageFromHash());

  // Public setter — always goes through the URL so browser back/forward
  // and bookmarks work. The hashchange listener mirrors back to state.
  const setCurrentPage = (key) => {
    if (typeof window !== 'undefined' && window.location.hash !== `#${key}`) {
      window.location.hash = `#${key}`;
    }
    setCurrentPageState(key);
  };

  // Sync state <-> URL: react to back/forward + manual address-bar edits.
  useEffect(() => {
    const handler = () => setCurrentPageState(getPageFromHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // Set default page to first accessible page when nothing is in the URL,
  // OR the URL points to a page the user can't access.
  useEffect(() => {
    if (!loading && !accessDenied && visibleNavItems.length > 0) {
      if (!currentPage || !visibleNavItems.find(item => item.key === currentPage)) {
        setCurrentPage(visibleNavItems[0].key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, accessDenied, visibleNavItems, currentPage]);

  // Show loading while permissions are being fetched
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Show access denied screen
  if (accessDenied) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'linear-gradient(135deg, #E63946 0%, #A4161A 100%)' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '40px', maxWidth: '450px', width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ color: '#333', marginBottom: '10px' }}>Access Denied</h2>
          <p style={{ color: '#666', marginBottom: '30px', lineHeight: '1.6' }}>
            Your account does not have access to this system. Please contact your administrator to request access.
          </p>
          <p style={{ color: '#999', fontSize: '13px', marginBottom: '20px' }}>
            Signed in as: {accounts?.[0]?.username || 'Unknown'}
          </p>
          <button
            onClick={() => instance.logoutPopup({ postLogoutRedirectUri: '/' })}
            style={{ background: 'linear-gradient(135deg, #E63946 0%, #A4161A 100%)', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-top">
          <div className="header-brand">
            <img src={hawksLogo} alt="Illawarra Hawks" className="header-logo" />
            <h1>SwishIQ</h1>
          </div>
          <UserProfile />
        </div>
        <nav className="nav">
          {visibleNavItems.map(item => (
            <button
              key={item.key}
              className={currentPage === item.key ? 'active' : ''}
              onClick={() => setCurrentPage(item.key)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="main-content">
        {currentPage === 'rosters' && <RosterView />}
        {currentPage === 'planning' && <GamePlanning />}
        {currentPage === 'coach-planner' && <CoachPlanner />}
        {currentPage === 'rostering' && <Rostering />}
        {currentPage === 'people' && <People />}
        {currentPage === 'message' && <Message />}
        {currentPage === 'reports' && <Reports />}
        {currentPage === 'payroll' && <Payroll />}
        {currentPage === 'admin' && <Admin />}
        {currentPage === 'help' && <Help />}
        {currentPage === 'judiciary' && <Judiciary />}
        {currentPage === 'swishiq' && (
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading SwishIQ Trials…</div>}>
            <SwishIqAdmin />
          </Suspense>
        )}
      </main>

      <footer className="footer">
        <p>&copy; 2026 Illawarra Hawks - SwishIQ</p>
      </footer>
    </div>
  );
}
